/**
 * Os pontos de cada pessoa: o `user_progress.dart` e o `xp_service.dart`
 * juntos.
 *
 * Vive um documento por pessoa em `xp_progress`, com o slug dela no lugar do
 * identificador — o mesmo arranjo do jardim, e pela mesma razão que o serviço
 * de lá escreveu no comentário: o armazém não tem subcoleção, e o progresso de
 * cada uma é um documento só.
 *
 * O que veio e o que não veio de lá:
 *
 *  - O `addXp` não veio para cá, e nem podia: no Flutter inteiro ninguém o
 *    chama, e por isso a barra de lá mostra nível 1 com zero ponto para sempre.
 *    Quem soma nesta casa é o servidor, em `server/src/xp.js`, na única porta
 *    por onde os quatro apps escrevem. A regra fica num lugar só; escrevê-la
 *    aqui seria três cópias com três chances de discordarem, e o celular, que
 *    carrega coleção por tela, contaria o que a tela leu.
 *
 *    Vale para o `streak` também. O `addXp` de lá escreve `streak: 1` só no
 *    ramo que cria o documento e nunca mais encosta no campo, então a sequência
 *    do Flutter é uma constante. A daqui anda, pela regra de dias do jardim e
 *    no fuso de quem escreveu.
 *
 *  - O `initializeProgress` veio como `garantirProgresso`, e quem o chama é a
 *    própria barra na primeira vez que aparece, e não o login. Dá no mesmo — a
 *    barra é a única coisa que lê este documento — e poupa mexer na sessão,
 *    que é caminho de todo mundo.
 */

import { COLECOES } from '../types/colecoes';
import { ligacao } from '../nucleo/ligacao';
import { gravarData, lerData } from '../nucleo/tempo';

/** Quanto ponto fecha um nível. O `/1000` que aparece em toda conta de lá. */
export const PONTOS_POR_NIVEL = 1000;

/** O `UserProgress`. */
export interface Progresso {
  /** O slug de quem. */
  quem: string;
  total: number;
  nivel: number;
  sequencia: number;
  ultimaVez: Date;
}

/** `UserProgress.fromMap`. */
export function progressoDeMapa(quem: string, dados: Record<string, unknown>): Progresso {
  return {
    quem,
    total: numero(dados.xpTotal, 0),
    nivel: numero(dados.level, 1),
    sequencia: numero(dados.streak, 0),
    ultimaVez: lerData(dados.lastActivity) ?? new Date(),
  };
}

/** `UserProgress.toMap`. */
export function progressoParaMapa(p: Progresso): Record<string, unknown> {
  return {
    xpTotal: p.total,
    level: p.nivel,
    streak: p.sequencia,
    lastActivity: gravarData(p.ultimaVez),
  };
}

/** Quanto ponto já andou dentro do nível de agora: o `xpTotal % 1000`. */
export function pontosNoNivel(total: number): number {
  return ((total % PONTOS_POR_NIVEL) + PONTOS_POR_NIVEL) % PONTOS_POR_NIVEL;
}

/** Quanto falta para o próximo: o `1000 - currentLevelXp`. */
export function pontosQueFaltam(total: number): number {
  return PONTOS_POR_NIVEL - pontosNoNivel(total);
}

/** O quanto da barra está cheio, entre 0 e 1. */
export function fatiaCheia(total: number): number {
  return Math.min(1, Math.max(0, pontosNoNivel(total) / PONTOS_POR_NIVEL));
}

/**
 * O `initializeProgress`: o vaso vazio de quem nunca passou por aqui.
 *
 * Não faz nada se o documento já existe, e por isso pode ser chamado toda vez
 * que a barra aparece. Devolve `false` quando não escreveu.
 *
 * A conferida é uma leitura do servidor, e não o `pronto` do armazém: `pronto`
 * fica verdadeiro assim que a primeira coleção chega, e `xp_progress` pode não
 * ser ela. Perguntar ao cache antes da hora seria ver vazio onde há progresso
 * — e gravar zero por cima. Se a leitura falhar, não escreve: melhor uma barra
 * a menos que um nível apagado.
 */
export async function garantirProgresso(quem: string): Promise<boolean> {
  if (!quem) return false;
  const { armazem } = ligacao();
  /* Se o documento já está na mão não há o que conferir: quem volta ao painel
     pela segunda vez não pede nada ao servidor. */
  if (armazem.documento(COLECOES.xp, quem) !== null) return false;
  try {
    await armazem.carregar(COLECOES.xp);
  } catch {
    return false;
  }
  if (armazem.documento(COLECOES.xp, quem) !== null) return false;
  await armazem.gravar(COLECOES.xp, quem, {
    xpTotal: 0,
    level: 1,
    streak: 0,
    lastActivity: gravarData(new Date()),
  });
  return true;
}

function numero(v: unknown, padrao: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? Math.trunc(v) : padrao;
}
